// "One visit, one invoice" (KAN-484, RD-BILL-014) — recognises the refusals the
// database raises when a write would double-bill a visit, or delete one that
// still sits on a live invoice, and turns them into plain, localized copy.
// Pure and dependency-free (no supabase client) so any layer — the shared
// error translator, a bulk runner, a toast — can use it.
//
// Server contract (supabase-js error: { code, message, details, hint }):
//   VISIT_ON_OTHER_INVOICE  23505  details JSON {job_id, invoice_id?, invoice_number?, invoice_number_year?, status?}
//   VISIT_OTHER_CUSTOMER    23514  details JSON {job_id}
//   VISIT_ON_DRAFT          23514  details JSON {invoice_id, invoice_number, invoice_number_year} (delete_job)
//   23503 on invoice_job_links      raw visit delete hit a visit on a live invoice
//                                   → treated as VISIT_ON_DRAFT without a number.

export const VISIT_GUARD_KINDS = {
  OTHER_INVOICE: 'OTHER_INVOICE',
  OTHER_CUSTOMER: 'OTHER_CUSTOMER',
  ON_DRAFT: 'ON_DRAFT',
};

// Same shape as invoiceService.formatInvoiceNumber, duplicated on purpose:
// that module imports the Supabase client (throws without env in plain Jest).
function formatLabel(num, year) {
  return `INV-${year}-${String(num).padStart(4, '0')}`;
}

function parseDetails(details) {
  if (typeof details !== 'string' || !details.trim()) return {};
  try {
    const parsed = JSON.parse(details);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

function invoiceLabelFrom(detail) {
  const num = detail.invoice_number;
  const year = detail.invoice_number_year;
  const okNum = (typeof num === 'number' && Number.isFinite(num)) || (typeof num === 'string' && /^\d+$/.test(num));
  const okYear = (typeof year === 'number' && Number.isFinite(year)) || (typeof year === 'string' && /^\d{4}$/.test(year));
  return okNum && okYear ? formatLabel(num, year) : null;
}

/**
 * @param {*} err supabase/Postgres error object, Error, or a bare message string
 * @returns {{ kind: string, invoiceLabel: string|null, invoiceId: string|null } | null}
 *   null for anything that is not one of the visit-guard refusals.
 */
export function parseVisitGuardError(err) {
  if (!err) return null;
  const isObj = typeof err === 'object';
  const message = String(isObj ? err.message ?? '' : err);
  const code = isObj ? String(err.code ?? '') : '';
  const detail = isObj ? parseDetails(err.details) : {};

  let kind = null;
  if (message.includes('VISIT_ON_OTHER_INVOICE')) kind = VISIT_GUARD_KINDS.OTHER_INVOICE;
  else if (message.includes('VISIT_OTHER_CUSTOMER')) kind = VISIT_GUARD_KINDS.OTHER_CUSTOMER;
  else if (message.includes('VISIT_ON_DRAFT')) kind = VISIT_GUARD_KINDS.ON_DRAFT;
  else if (message.includes('invoice_job_links') && (code === '23503' || /foreign key/i.test(message))) {
    kind = VISIT_GUARD_KINDS.ON_DRAFT;
  }
  if (!kind) return null;

  return {
    kind,
    invoiceLabel: kind === VISIT_GUARD_KINDS.OTHER_CUSTOMER ? null : invoiceLabelFrom(detail),
    invoiceId: typeof detail.invoice_id === 'string' ? detail.invoice_id : null,
  };
}

const DESCRIPTORS = {
  otherInvoice: { id: 'errors.visitGuard.otherInvoice', defaultMessage: 'This visit is already on {invoice}.' },
  otherInvoiceNoNumber: { id: 'errors.visitGuard.otherInvoiceNoNumber', defaultMessage: 'This visit is already on another invoice.' },
  otherCustomer: { id: 'errors.visitGuard.otherCustomer', defaultMessage: 'This visit belongs to another customer.' },
  onDraft: { id: 'errors.visitGuard.onDraft', defaultMessage: 'This visit is on draft {invoice}. Remove it from the draft first, then delete the visit.' },
  onDraftNoNumber: { id: 'errors.visitGuard.onDraftNoNumber', defaultMessage: 'This visit is on a draft invoice. Remove it from the draft first, then delete the visit.' },
};

/** @returns {{ id: string, defaultMessage: string, values: object } | null} */
export function visitGuardDescriptor(err) {
  const p = parseVisitGuardError(err);
  if (!p) return null;
  const values = p.invoiceLabel ? { invoice: p.invoiceLabel } : {};
  if (p.kind === VISIT_GUARD_KINDS.OTHER_CUSTOMER) return { ...DESCRIPTORS.otherCustomer, values };
  if (p.kind === VISIT_GUARD_KINDS.OTHER_INVOICE) {
    return { ...(p.invoiceLabel ? DESCRIPTORS.otherInvoice : DESCRIPTORS.otherInvoiceNoNumber), values };
  }
  return { ...(p.invoiceLabel ? DESCRIPTORS.onDraft : DESCRIPTORS.onDraftNoNumber), values };
}

/** Localized message via a react-intl-shaped `intl`, or null when `err` is not a visit-guard refusal. */
export function visitGuardMessage(err, intl) {
  const d = visitGuardDescriptor(err);
  if (!d) return null;
  return intl.formatMessage({ id: d.id, defaultMessage: d.defaultMessage }, d.values);
}

/**
 * The send-invoice-email edge function's 409 body for a claim UPDATE the link
 * trigger refused: `{ code: 'VISIT_ON_OTHER_INVOICE', detail?: { invoice } }`
 * (`invoice` = "INV-2026-0123", already formatted server-side). Returns the same
 * "This visit is already on {invoice}." descriptor the direct DB refusal uses,
 * or null for any other body. Shared by the single (admin + billing) and bulk
 * send paths.
 *
 * @returns {{ id: string, defaultMessage: string, values: object } | null}
 */
export function visitOnOtherInvoiceBodyDescriptor(body) {
  if (!body || (body.code !== 'VISIT_ON_OTHER_INVOICE' && body.error !== 'VISIT_ON_OTHER_INVOICE')) return null;
  const invoice = typeof body.detail?.invoice === 'string' && /^INV-\d{4}-\d+$/.test(body.detail.invoice)
    ? body.detail.invoice
    : null;
  return invoice
    ? { ...DESCRIPTORS.otherInvoice, values: { invoice } }
    : { ...DESCRIPTORS.otherInvoiceNoNumber, values: {} };
}

/**
 * clone_voided_manual_invoice_draft refuses a second live copy of a voided
 * invoice: message CLONE_ALREADY_LIVE, 23505, constraint
 * invoices_clone_source_live_uq, DETAIL JSON of the existing copy
 * `{invoice_id, invoice_number, invoice_number_year, status}`. DETAIL is parsed
 * defensively; no usable number -> number-less copy.
 *
 * @returns {{ id: string, defaultMessage: string, values: object } | null}
 */
export function cloneAlreadyLiveDescriptor(err) {
  if (!err || typeof err !== 'object') return null;
  const hay = `${err.message ?? ''} ${err.details ?? ''}`;
  if (!hay.includes('CLONE_ALREADY_LIVE') && !hay.includes('invoices_clone_source_live_uq')) return null;
  const label = invoiceLabelFrom(parseDetails(err.details));
  return label
    ? { id: 'billing.invoiceCrud.errorCloneAlreadyLive', defaultMessage: 'This invoice was already re-issued as {invoice}.', values: { invoice: label } }
    : { id: 'billing.invoiceCrud.errorCloneAlreadyLiveNoNumber', defaultMessage: 'This invoice was already re-issued.', values: {} };
}
